import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import SchedulePicker, { parseSchedule } from "../../components/SchedulePicker";
import { syLabel } from "../../utils/schoolYear";
import s from "./SchedulePage.module.css";

const DAYS = [
  { key: "Mon", label: "Monday" },
  { key: "Tue", label: "Tuesday" },
  { key: "Wed", label: "Wednesday" },
  { key: "Thu", label: "Thursday" },
  { key: "Fri", label: "Friday" },
];
/** 7am–6pm covers every realistic school-day slot and matches SchedulePicker. */
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

const TEACHER_STAFF_TYPES = ["TEACHING", "TEACHER"];

/** Roles that can edit class offerings from this page (matches
 *  AdminGuard.java on the backend — AcademicHead, Admin, SuperAdmin). */
const EDITOR_ROLES = new Set(["AcademicHead", "Admin", "SuperAdmin"]);

/** Roles that pick a person from a sidebar (they view many schedules).
 *  Other roles see their own / their kid's schedule with no picker. */
const PICKER_ROLES = new Set(["AcademicHead", "Admin", "SuperAdmin"]);

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}
function authHeaders() {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
  };
}

/** Subject display name — Subject has `title` (and `code`), no `name`. */
function subjectLabel(subj, id) {
  return subj?.title || subj?.code || `Subject #${id}`;
}

/** Room name — prefers the Room entity (roomId), falls back to the
 *  legacy free-text `room` column on older rows. */
function roomLabel(o, roomById) {
  return (o.roomId != null && roomById[o.roomId]?.name) || o.room || "";
}

/** "13:00" → "1:00 PM" */
function fmtTime(t) {
  const m = toMinutes(t);
  if (m == null) return "";
  const h = Math.floor(m / 60), mm = String(m % 60).padStart(2, "0");
  return `${((h + 11) % 12) + 1}:${mm} ${h >= 12 ? "PM" : "AM"}`;
}

function isTeacher(emp) {
  return emp && TEACHER_STAFF_TYPES.includes(emp.staffType);
}

function toMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
}

/** Turn a list of class offerings into Mon–Fri placements keyed by start
 *  hour. Each offering's schedule ("Mon/Wed 08:00-09:00") is exploded into
 *  one block per day it mentions. Rows the start doesn't align to are
 *  skipped; the block spans however many hour rows the duration covers. */
function buildPlacements(offerings) {
  const byDay = Object.fromEntries(DAYS.map(d => [d.key, []]));
  for (const o of offerings) {
    const parsed = parseSchedule(o.schedule);
    const startMin = toMinutes(parsed.start);
    const endMin   = toMinutes(parsed.end);
    if (startMin == null || endMin == null) continue;
    const startHour = Math.floor(startMin / 60);
    const endHour   = Math.ceil(endMin   / 60);
    for (const d of parsed.days) {
      if (!byDay[d]) continue;
      byDay[d].push({
        offering: o,
        startHour,
        endHour,
        rowSpan: Math.max(1, endHour - startHour),
      });
    }
  }
  return byDay;
}

export default function SchedulePage() {
  const user = getUser();
  const role = user?.role;
  const canEdit = EDITOR_ROLES.has(role);
  const hasPicker = PICKER_ROLES.has(role);

  const [employees,   setEmployees]   = useState([]);
  const [students,    setStudents]    = useState([]);
  const [semesters,   setSemesters]   = useState([]);
  const [sections,    setSections]    = useState([]);
  const [subjects,    setSubjects]    = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [offerings,   setOfferings]   = useState([]);
  const [rooms,       setRooms]       = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  // Read-only details popup for a clicked class (non-editors).
  const [viewing,     setViewing]     = useState(null);
  const [children,    setChildren]    = useState([]);    // Parent: {studentId}
  const [semesterId,  setSemesterId]  = useState("");
  const [selectedId,  setSelectedId]  = useState(null);  // picker: employee.id; parent: student.id
  const [modal,       setModal]       = useState(null);
  const [err,         setErr]         = useState("");

  // ── Lookups. Students are needed for Admin/SuperAdmin/AcademicHead to
  // resolve a section schedule too, so we always load them — cheap. ──
  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
    ]).then(([emp, stu, sem, sec, sub, gl, rm, sy]) => {
      setRooms(Array.isArray(rm) ? rm : []);
      setSchoolYears(Array.isArray(sy) ? sy : []);
      setEmployees(Array.isArray(emp) ? emp : []);
      setStudents(Array.isArray(stu) ? stu : []);
      setSemesters(Array.isArray(sem) ? sem : []);
      setSections(Array.isArray(sec) ? sec : []);
      setSubjects(Array.isArray(sub) ? sub : []);
      setGradeLevels(Array.isArray(gl) ? gl : []);
      if (Array.isArray(sem) && sem.length > 0) {
        setSemesterId(String(sem[sem.length - 1].id));
      }
    });
  }, []);

  // Parent: fetch their children once, and auto-select the first.
  useEffect(() => {
    if (role !== "Parent" || !user?.linkedEntityId) return;
    fetch(`${API_BASE}/api/parent-student?parentId=${user.linkedEntityId}`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : [];
        setChildren(arr);
        if (arr.length > 0) setSelectedId(arr[0].studentId);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user?.linkedEntityId]);

  // Teacher/Student: pre-select themselves so the grid shows immediately.
  useEffect(() => {
    if (role === "Teacher" && user?.linkedEntityType === "STAFF" && user?.linkedEntityId) {
      setSelectedId(user.linkedEntityId);
    } else if (role === "Student" && user?.linkedEntityType === "STUDENT" && user?.linkedEntityId) {
      setSelectedId(user.linkedEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user?.linkedEntityId]);

  // Offerings reload whenever the active semester changes.
  useEffect(() => {
    if (!semesterId) { setOfferings([]); return; }
    fetch(`${API_BASE}/api/class-offerings?semesterId=${semesterId}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setOfferings(Array.isArray(data) ? data : []));
  }, [semesterId]);

  const sectionById = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);
  const gradeById   = useMemo(() => Object.fromEntries(gradeLevels.map(x => [x.id, x])), [gradeLevels]);
  const studentById = useMemo(() => Object.fromEntries(students.map(x => [x.id, x])), [students]);
  const roomById    = useMemo(() => Object.fromEntries(rooms.map(x => [x.id, x])), [rooms]);

  /** Sidebar list for picker roles. The Class Schedule is conceptually
   *  about teaching assignments — non-teaching staff (Cashier, Janitor,
   *  Security, etc.) don't have classes, so showing them in the picker
   *  was a dead-end ("click → empty info card"). Every picker role now
   *  sees a single, clean "Teachers" list. */
  const sidebarTeachers = useMemo(() => {
    if (!hasPicker) return [];
    return employees.filter(isTeacher)
      .slice()
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [employees, hasPicker]);

  /** Resolve what we're actually showing: a teacher's weekly grid, a
   *  student's section schedule, or an info card — depending on role and
   *  the current selection. The grid's `offeringsShown` is pre-filtered so
   *  WeeklyGrid stays dumb. */
  const display = useMemo(() => {
    if (!selectedId) return { kind: "empty" };

    // Picker roles: selectedId is a teacher's employee.id (sidebar is
    // teachers-only). Anything else → fall through to empty.
    if (hasPicker) {
      const emp = employees.find(e => e.id === selectedId);
      if (!emp || !isTeacher(emp)) return { kind: "empty" };
      return {
        kind: "teacherGrid",
        employee: emp,
        subtitle: "Mon–Fri schedule · auto-synced from Academics",
        offeringsShown: offerings.filter(o => o.teacherStaffId === emp.id),
        canEditHere: canEdit,
      };
    }

    // Teacher: own grid (linkedEntityId is their Employee.id).
    if (role === "Teacher") {
      const emp = employees.find(e => e.id === selectedId);
      return {
        kind: "teacherGrid",
        employee: emp,
        subtitle: "Your Mon–Fri class schedule",
        offeringsShown: offerings.filter(o => o.teacherStaffId === selectedId),
        canEditHere: false,
      };
    }

    // Student / Parent: selectedId is a Student.id. Use currentSectionId to
    // show the section's entire weekly schedule (every subject the class
    // attends, with its assigned teacher).
    if (role === "Student" || role === "Parent") {
      const stu = studentById[selectedId];
      if (!stu) return { kind: "empty" };
      if (!stu.currentSectionId) {
        return { kind: "noSection", student: stu };
      }
      const sec = sectionById[stu.currentSectionId];
      const grade = sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
      const title = `${stu.firstName || ""} ${stu.lastName || ""}`.trim() || "Student";
      const sub = sec
        ? `${grade?.name ? grade.name + " · " : ""}${sec.name} — Mon–Fri class schedule`
        : "Mon–Fri class schedule";
      return {
        kind: "sectionGrid",
        title, subtitle: sub,
        offeringsShown: offerings.filter(o => o.sectionId === stu.currentSectionId),
        canEditHere: false,
      };
    }

    return { kind: "empty" };
  }, [selectedId, hasPicker, employees, offerings, role, studentById, sectionById, gradeById, canEdit]);

  const placements = useMemo(() => {
    return display.kind === "teacherGrid" || display.kind === "sectionGrid"
      ? buildPlacements(display.offeringsShown)
      : Object.fromEntries(DAYS.map(d => [d.key, []]));
  }, [display]);

  async function saveOffering(payload, editingId) {
    setErr("");
    try {
      const url = editingId
        ? `${API_BASE}/api/class-offerings/${editingId}`
        : `${API_BASE}/api/class-offerings`;
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      const fresh = await fetch(`${API_BASE}/api/class-offerings?semesterId=${semesterId}`)
        .then(r => r.ok ? r.json() : []);
      setOfferings(Array.isArray(fresh) ? fresh : []);
      setModal(null);
    } catch (e) { setErr(e.message); }
  }

  async function deleteOffering(id) {
    if (!window.confirm("Remove this class from the schedule?")) return;
    setErr("");
    try {
      const res = await fetch(`${API_BASE}/api/class-offerings/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setOfferings(prev => prev.filter(o => o.id !== id));
      setModal(null);
    } catch (e) { setErr(e.message); }
  }

  // ── Heading text for the main pane based on display state. ──
  const paneTitle = (() => {
    if (display.kind === "teacherGrid") return display.employee?.name || "Teacher";
    if (display.kind === "sectionGrid") return display.title;
    if (display.kind === "noSection")   return `${display.student?.firstName || ""} ${display.student?.lastName || ""}`.trim();
    if (hasPicker)                      return "Select a teacher";
    if (role === "Parent")              return "Select a child";
    return "Schedule";
  })();
  const paneSub = display.subtitle || "";

  // Initials for the sidebar avatar — first + last initial, falls back to "?"
  function initialsOf(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last  = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase() || "?";
  }

  return (
    <div className={s.page}>
      <header className={s.pageHeader}>
        <div>
          <div className={s.eyebrow}>Class Schedule</div>
          <h1 className={s.pageTitle}>
            {hasPicker
              ? "Mon–Fri teaching grid"
              : role === "Teacher"
              ? "My teaching schedule"
              : role === "Student"
              ? "My class schedule"
              : "Class schedule"}
          </h1>
          <p className={s.pageSub}>
            {hasPicker
              ? "Pick a teacher to review or edit their weekly class plan for the selected semester."
              : role === "Teacher"
              ? "Your class plan, auto-synced from Academics."
              : role === "Student" || role === "Parent"
              ? "Your section's weekly class schedule."
              : "Mon–Fri class plan for the selected semester."}
          </p>
        </div>
      </header>

      {/* Sidebar only renders when the user can pick another person:
       *   - hasPicker = admin-tier teacher picker
       *   - parent with 2+ children = child switcher
       * For every other case (single-child parent, student, lone teacher)
       * we collapse the wrap to 1fr so the schedule grid takes the full
       * available width — otherwise the empty 280px column on the left
       * eats into the grid and Tue–Fri overflows past the visible card. */}
      <div
        className={s.wrap}
        style={
          (hasPicker || (role === "Parent" && children.length > 1))
            ? undefined
            : { gridTemplateColumns: "1fr" }
        }>
        {hasPicker && (
          <aside className={s.sidebar}>
            <div className={s.sidebarHead}>
              <span>Teachers</span>
              <span className={s.sidebarCount}>{sidebarTeachers.length}</span>
            </div>
            {sidebarTeachers.length === 0 ? (
              <div className={s.sideEmpty}>Loading teachers…</div>
            ) : (
              <div className={s.personList}>
                {sidebarTeachers.map(emp => {
                  const active = selectedId === emp.id;
                  return (
                    <button key={emp.id}
                            className={`${s.personBtn} ${active ? s.personBtnActive : ""}`}
                            onClick={() => setSelectedId(emp.id)}>
                      <span className={s.personAvatar}>{initialsOf(emp.name)}</span>
                      <span className={s.personMeta}>
                        <span className={s.personName}>{emp.name || "(unnamed)"}</span>
                        {emp.department && (
                          <span className={s.personRole}>{emp.department}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </aside>
        )}

        {role === "Parent" && children.length > 1 && (
          <aside className={s.sidebar}>
            <div className={s.sidebarHead}>Children</div>
            {children.map(c => {
              const stu = studentById[c.studentId];
              const active = selectedId === c.studentId;
              return (
                <button key={c.studentId}
                        className={`${s.personBtn} ${active ? s.personBtnActive : ""}`}
                        onClick={() => setSelectedId(c.studentId)}>
                  {stu ? `${stu.firstName || ""} ${stu.lastName || ""}`.trim() : `Student #${c.studentId}`}
                </button>
              );
            })}
          </aside>
        )}

        <section className={s.main}>
          <div className={s.mainHead}>
            <div>
              <div className={s.mainTitle}>{paneTitle}</div>
              <div className={s.mainSub}>{paneSub}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select className={s.semesterSelect}
                      value={semesterId}
                      onChange={e => setSemesterId(e.target.value)}>
                <option value="">— select semester —</option>
                {semesters.map(sem => (
                  <option key={sem.id} value={sem.id}>{sem.label}</option>
                ))}
              </select>
              {display.canEditHere && display.kind === "teacherGrid" && semesterId && (
                <button className={s.addBtn}
                        onClick={() => setModal({ mode: "add" })}>
                  + Add subject
                </button>
              )}
            </div>
          </div>

          {err && <div className={s.err}>{err}</div>}

          {display.kind === "empty" && (
            <div className={s.empty}>
              <div className={s.emptyIcon} aria-hidden="true">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.6"
                     strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="16" rx="2.5" />
                  <path d="M16 3v4M8 3v4M3 10h18" />
                </svg>
              </div>
              <div className={s.emptyTitle}>
                {role === "Parent" && children.length === 0
                  ? "No linked children"
                  : "Pick a teacher to begin"}
              </div>
              <div className={s.emptyBody}>
                {hasPicker
                  ? "Choose a teacher from the list to view their Mon–Fri schedule for the selected semester. Non-teaching staff don't have class assignments."
                  : role === "Parent"
                  ? "This account isn't linked to any students yet. Contact the registrar."
                  : "Loading your schedule…"}
              </div>
            </div>
          )}

          {display.kind === "noSection" && (
            <div className={s.empty}>
              <div className={s.emptyTitle}>No section assigned</div>
              <div className={s.emptyBody}>
                This student isn't currently enrolled in a section — there's no weekly schedule to show yet.
              </div>
            </div>
          )}

          {(display.kind === "teacherGrid" || display.kind === "sectionGrid") && !semesterId && (
            <div className={s.empty}>
              <div className={s.emptyTitle}>Pick a semester</div>
              <div className={s.emptyBody}>The weekly grid is scoped to a single semester.</div>
            </div>
          )}

          {/* Grid OR empty-card. We only render the WeeklyGrid when there
              are real offerings to place; an empty 7am-6pm grid is
              confusing UX (the student/teacher can't tell whether they
              should be seeing classes or not). When the section / teacher
              has zero offerings for this semester, show a premium
              empty-state card with a clear "who to contact" hint. */}
          {(display.kind === "teacherGrid" || display.kind === "sectionGrid") && semesterId && (
            display.offeringsShown.length === 0 ? (
              <div className={s.empty}>
                <div className={s.emptyIcon} aria-hidden="true">
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="1.6"
                       strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="5" width="18" height="16" rx="2.5"/>
                    <path d="M16 3v4M8 3v4M3 10h18"/>
                    <line x1="9" y1="14" x2="15" y2="14"/>
                    <line x1="9" y1="17" x2="13" y2="17"/>
                  </svg>
                </div>
                <div className={s.emptyTitle}>
                  {display.kind === "sectionGrid"
                    ? (role === "Parent"
                        ? `${display.title}'s section isn't scheduled yet`
                        : "Your section isn't scheduled yet")
                    : "No classes assigned"}
                </div>
                <div className={s.emptyBody}>
                  {display.kind === "sectionGrid" ? (
                    <>
                      {display.subtitle && (
                        <>
                          <strong>{display.subtitle.replace(/ — Mon[–-]Fri class schedule$/, "")}</strong>
                          {" "}has no subjects added to the school's weekly schedule yet.
                        </>
                      )}
                      {!display.subtitle && (
                        <>This section has no subjects added to the school's weekly schedule yet.</>
                      )}
                      <br />
                      Once the registrar (or Academic Head) adds class offerings
                      — a <em>subject + teacher + schedule</em> — for this
                      section, they'll appear here automatically.
                    </>
                  ) : (
                    <>
                      You don't have any class offerings linked to you for
                      this semester yet. Ask an admin to assign your
                      classes in <em>Academic Directory → Sections</em>.
                    </>
                  )}
                </div>
              </div>
            ) : (
              <WeeklyGrid
                placements={placements}
                sectionById={sectionById}
                subjectById={subjectById}
                gradeById={gradeById}
                employees={employees}
                roomById={roomById}
                showTeacher={display.kind === "sectionGrid"}
                // Editors open the edit form; everyone else gets the
                // read-only class details.
                onClick={display.canEditHere
                  ? (offering => setModal({ mode: "edit", offering }))
                  : (offering => setViewing(offering))}
              />
            )
          )}
        </section>
      </div>

      {viewing && (
        <ClassDetailModal
          offering={viewing}
          subject={subjectById[viewing.subjectId]}
          section={sectionById[viewing.sectionId]}
          gradeById={gradeById}
          teacher={employees.find(e => e.id === viewing.teacherStaffId)}
          room={roomLabel(viewing, roomById)}
          semester={semesters.find(x => x.id === viewing.semesterId)}
          schoolYears={schoolYears}
          onClose={() => setViewing(null)}
        />
      )}

      {modal && canEdit && display.kind === "teacherGrid" && (
        <OfferingModal
          mode={modal.mode}
          initial={modal.offering}
          teacherId={display.employee?.id}
          semesterId={Number(semesterId)}
          sections={sections}
          subjects={subjects}
          gradeById={gradeById}
          onClose={() => setModal(null)}
          onSave={saveOffering}
          onDelete={deleteOffering}
        />
      )}
    </div>
  );
}

function WeeklyGrid({ placements, sectionById, subjectById, gradeById, employees, roomById = {}, showTeacher, onClick }) {
  const teacherById = useMemo(
    () => Object.fromEntries((employees || []).map(e => [e.id, e])),
    [employees]
  );
  return (
    <div className={s.grid}>
      <div className={s.gridHeadBlank} />
      {DAYS.map(d => <div key={d.key} className={s.gridHead}>{d.label}</div>)}
      {HOURS.map(h => {
        const label = `${h > 12 ? h - 12 : h}${h >= 12 ? "pm" : "am"}`;
        return (
          <React.Fragment key={`row-${h}`}>
            <div className={s.hourCell}>{label}</div>
            {DAYS.map(d => {
              const blocks = (placements[d.key] || []).filter(b => b.startHour === h);
              return (
                <div key={`${d.key}-${h}`} className={s.slotCell}>
                  {blocks.map(b => {
                    const o = b.offering;
                    const subj = subjectById[o.subjectId];
                    const sec  = sectionById[o.sectionId];
                    const grade = sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                    const teacher = o.teacherStaffId ? teacherById[o.teacherStaffId] : null;
                    const room = roomLabel(o, roomById);
                    const metaBits = showTeacher
                      ? [teacher?.name, room].filter(Boolean)
                      : [grade?.name, sec?.name, room].filter(Boolean);
                    return (
                      <button key={o.id}
                           type="button"
                           className={s.classBlock}
                           style={{
                             minHeight: b.rowSpan * 48,
                             cursor: onClick ? "pointer" : "default",
                           }}
                           onClick={onClick ? (() => onClick(o)) : undefined}>
                        <div className={s.classSubject}>{subjectLabel(subj, o.subjectId)}</div>
                        <div className={s.classMeta}>{metaBits.join(" · ")}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function OfferingModal({
  mode, initial, teacherId, semesterId,
  sections, subjects, gradeById,
  onClose, onSave, onDelete,
}) {
  const [sectionId, setSectionId] = useState(initial?.sectionId ?? "");
  const [subjectId, setSubjectId] = useState(initial?.subjectId ?? "");
  const [schedule,  setSchedule]  = useState(initial?.schedule ?? "");
  const [room,      setRoom]      = useState(initial?.room ?? "");
  const [busy,      setBusy]      = useState(false);
  const [localErr,  setLocalErr]  = useState("");

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [busy, onClose]);

  async function submit(e) {
    e.preventDefault();
    setLocalErr("");
    if (!sectionId || !subjectId) {
      setLocalErr("Section and subject are required.");
      return;
    }
    setBusy(true);
    try {
      await onSave({
        semesterId,
        sectionId: Number(sectionId),
        subjectId: Number(subjectId),
        teacherStaffId: teacherId,
        schedule: schedule || null,
        room:     room || null,
      }, initial?.id);
    } catch (e2) { setLocalErr(e2.message); }
    finally { setBusy(false); }
  }

  return createPortal(
    <div className={s.modalOverlay}>
      <div className={s.modal} onClick={e => e.stopPropagation()}>
        <div className={s.modalHead}>
          <h3 className={s.modalTitle}>
            {mode === "add" ? "Add subject to schedule" : "Edit scheduled subject"}
          </h3>
          <button type="button" className={s.modalClose}
                  onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <form onSubmit={submit}>
          <div className={s.modalBody}>
            {localErr && <div className={s.err}>{localErr}</div>}

            <div className={s.field}>
              <label className={s.fieldLabel}>Section *</label>
              <select className={s.fieldInput}
                      value={sectionId}
                      onChange={e => setSectionId(e.target.value)}>
                <option value="">— select —</option>
                {sections.map(sec => {
                  const grade = sec.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                  return (
                    <option key={sec.id} value={sec.id}>
                      {grade?.name ? `${grade.name} · ${sec.name}` : sec.name}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className={s.field}>
              <label className={s.fieldLabel}>Subject *</label>
              <select className={s.fieldInput}
                      value={subjectId}
                      onChange={e => setSubjectId(e.target.value)}>
                <option value="">— select —</option>
                {subjects.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.code ? `${sub.code} · ` : ""}{subjectLabel(sub, sub.id)}</option>
                ))}
              </select>
            </div>

            <div className={s.field}>
              <label className={s.fieldLabel}>Schedule</label>
              <SchedulePicker value={schedule} onChange={setSchedule} />
            </div>

            <div className={s.field}>
              <label className={s.fieldLabel}>Room</label>
              <input className={s.fieldInput}
                     type="text" value={room}
                     onChange={e => setRoom(e.target.value)}
                     placeholder="Room 101" />
            </div>
          </div>
          <div className={s.modalFoot}>
            {mode === "edit" && initial?.id && (
              <button type="button" className={s.btnDanger}
                      disabled={busy}
                      onClick={() => onDelete(initial.id)}>
                Remove
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className={s.btnGhost}
                    onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className={s.btnPrimary} disabled={busy}>
              {busy ? "Saving…" : (mode === "add" ? "Add" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

/** Read-only details for one class block — what students, parents and
 *  teachers see when they click a subject on the grid. */
function ClassDetailModal({ offering: o, subject, section, gradeById, teacher, room, semester, schoolYears, onClose }) {
  useEffect(() => {
    function onKeyDown(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const parsed = parseSchedule(o.schedule);
  const dayNames = parsed.days.map(k => DAYS.find(d => d.key === k)?.label || k);
  const time = parsed.start && parsed.end ? `${fmtTime(parsed.start)} – ${fmtTime(parsed.end)}` : "";
  const grade = section?.gradeLevelId ? gradeById[section.gradeLevelId] : null;
  const sy = semester ? schoolYears.find(y => y.id === semester.schoolYearId) : null;

  const fields = [
    ["Subject code", subject?.code || "—"],
    ["Units",        subject?.units != null ? String(subject.units) : "—"],
    ["Teacher",      teacher?.name || "To be assigned"],
    ["Section",      [grade?.name, section?.name].filter(Boolean).join(" · ") || "—"],
    ["Days",         dayNames.join(", ") || "—"],
    ["Time",         time || "—"],
    ["Room",         room || "—"],
    ["Term",         [sy?.label && syLabel(sy.label), semester?.label && `${semester.label} semester`].filter(Boolean).join(" · ") || "—"],
  ];

  return createPortal(
    <div className={s.modalOverlay} onClick={onClose}>
      <div className={s.modal} onClick={e => e.stopPropagation()}
           role="dialog" aria-modal="true" aria-labelledby="cdm-title">
        <div className={s.modalHead}>
          <h3 id="cdm-title" className={s.modalTitle}>{subjectLabel(subject, o.subjectId)}</h3>
          <button type="button" className={s.modalClose} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className={s.modalBody}>
          <dl className={s.detailList}>
            {fields.map(([k, v]) => (
              <div key={k} className={s.detailItem}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className={s.modalFoot}>
          <div style={{ flex: 1 }} />
          <button type="button" className={s.btnGhost} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
